import { Link, useRouter } from '@tanstack/react-router';
import { ChevronDown, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { Product } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@nucleus-commerce/ui/components/tooltip';

import { useProductTypes } from '@/features/dictionary';
import { isLiveInStore, useProductStoreRows } from '@/features/store-catalog';

import { useAdjacentProducts } from '../hooks/use-adjacent-products';
import type { ProductFormInput } from '../schemas/product-form-schema';

import { DeleteProductModal } from './delete-product-modal';
import { DuplicateProductDialog } from './duplicate-product-dialog';
import { ProductStatusBadge } from './product-status-badge';

/** Opens the product where it is live; a menu because it can be live in several stores. */
function ViewOnStorefrontMenu({ product }: { product: Product }) {
  const { t } = useTranslation('products');
  const { rows } = useProductStoreRows(product.id);
  const live = rows.flatMap(({ store, row }) =>
    row?.storefrontUrl && isLiveInStore(row, product.status)
      ? [{ store, url: row.storefrontUrl }]
      : [],
  );

  if (live.length === 0) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {/* aria-disabled rather than disabled so the reason stays reachable by hover and focus. */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="cursor-not-allowed opacity-50"
              aria-disabled="true"
              onClick={(event) => event.preventDefault()}
            >
              <ExternalLink aria-hidden />
              {t('masterPage.storefront.view')}
            </Button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{t('masterPage.storefront.notLive')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <ExternalLink aria-hidden />
          {t('masterPage.storefront.view')}
          <ChevronDown aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {live.map(({ store, url }) => (
          <DropdownMenuItem key={store.id} asChild>
            <a href={url} target="_blank" rel="noopener noreferrer">
              {store.name}
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MenuItemBody({ title, hint }: { title: string; hint?: string }) {
  return (
    <span className="flex flex-col">
      <span className="font-medium">{title}</span>
      {hint ? <span className="text-muted-foreground text-xs">{hint}</span> : null}
    </span>
  );
}

function MoreActionsMenu({ product }: { product: Product }) {
  const { t } = useTranslation('products');
  const router = useRouter();
  const { can } = usePermissions();
  const form = useFormContext<ProductFormInput>();
  const status = useWatch({ control: form.control, name: 'status' });
  const { rows } = useProductStoreRows(product.id);
  const [duplicating, setDuplicating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const storeCount = rows.filter(({ row }) => row).length;
  const canWrite = can('products.write');

  // One path to save: these only change the Status field, and the save bar takes it from there.
  const setStatus = (next: ProductFormInput['status']) => {
    form.setValue('status', next, { shouldDirty: true, shouldValidate: true });
  };
  const archive = () => setStatus('archived');
  // A product that was only marked archived here goes back; an archived one wakes up as a draft.
  const unarchive = () => setStatus(product.status === 'archived' ? 'draft' : product.status);

  const copyLink = async () => {
    const { href } = router.buildLocation({
      to: '/org/products/$productId',
      params: { productId: product.id },
    });
    try {
      await navigator.clipboard.writeText(new URL(href, window.location.origin).href);
      toast.success(t('masterPage.more.linkCopied'));
    } catch {
      toast.error(t('masterPage.more.linkNotCopied'));
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="sm">
            {t('masterPage.more.label')}
            <ChevronDown aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          {canWrite ? (
            <DropdownMenuItem onSelect={() => setDuplicating(true)}>
              <MenuItemBody
                title={t('masterPage.more.duplicate')}
                hint={t('masterPage.more.duplicateHint')}
              />
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onSelect={() => void copyLink()}>
            <MenuItemBody title={t('masterPage.more.copyLink')} />
          </DropdownMenuItem>
          {can('audit.read') ? (
            <DropdownMenuItem asChild>
              <Link to="/org/activity" search={{ entityType: 'product', entityId: product.id }}>
                <MenuItemBody
                  title={t('masterPage.more.history')}
                  hint={t('masterPage.more.historyHint')}
                />
              </Link>
            </DropdownMenuItem>
          ) : null}
          {canWrite ? (
            <>
              <DropdownMenuSeparator />
              {status === 'archived' ? (
                <DropdownMenuItem onSelect={unarchive}>
                  <MenuItemBody
                    title={t('masterPage.more.unarchive')}
                    hint={t('masterPage.more.unarchiveHint')}
                  />
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onSelect={archive}>
                  <MenuItemBody
                    title={t('masterPage.more.archive')}
                    hint={t('masterPage.more.archiveHint')}
                  />
                </DropdownMenuItem>
              )}
              <DropdownMenuItem variant="destructive" onSelect={() => setDeleting(true)}>
                <MenuItemBody
                  title={t('masterPage.more.delete')}
                  hint={t('masterPage.more.deleteHint', { count: storeCount })}
                />
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <Can permission="products.write">
        <DuplicateProductDialog
          product={product}
          open={duplicating}
          onOpenChange={setDuplicating}
        />
        <DeleteProductModal
          product={product}
          open={deleting}
          onOpenChange={setDeleting}
          onArchive={() => {
            archive();
            setDeleting(false);
          }}
        />
      </Can>
    </>
  );
}

/** Previous and next product of the list the person came from; nothing without one. */
function ProductStepper({ productId }: { productId: string }) {
  const { t } = useTranslation('products');
  const adjacent = useAdjacentProducts(productId);
  if (!adjacent) return null;
  const step = (id: string | null, label: string, icon: React.ReactNode) =>
    id ? (
      <Button asChild variant="outline" size="icon-sm">
        <Link to="/org/products/$productId" params={{ productId: id }} aria-label={label}>
          {icon}
        </Link>
      </Button>
    ) : (
      <Button type="button" variant="outline" size="icon-sm" disabled aria-label={label}>
        {icon}
      </Button>
    );
  return (
    <span className="flex items-center gap-1">
      {step(adjacent.previousId, t('masterPage.stepper.previous'), <ChevronLeft aria-hidden />)}
      {step(adjacent.nextId, t('masterPage.stepper.next'), <ChevronRight aria-hidden />)}
    </span>
  );
}

/**
 * The master page's header. It sits inside the form's context so Archive can set the Status field,
 * while the title and status badge keep saying what is saved.
 */
export function ProductPageHeader({ product }: { product: Product }) {
  const { t } = useTranslation('products');
  const format = useFormat();
  const productTypes = useProductTypes();
  const typeName = productTypes.data?.find((type) => type.id === product.productTypeId)?.name;

  return (
    <PageHeader
      tour="catalog.product-edit.header"
      title={product.title}
      meta={<ProductStatusBadge status={product.status} />}
      description={
        <span title={format.utc(product.updatedAt)}>
          {[
            product.skuRoot,
            typeName,
            product.vendor,
            t('masterPage.updated', { when: format.relative(product.updatedAt) }),
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      }
      backTo={{ to: '/org/products' }}
      backLabel={t('masterPage.back')}
      actions={
        <>
          <ViewOnStorefrontMenu product={product} />
          <MoreActionsMenu product={product} />
          <ProductStepper productId={product.id} />
        </>
      }
    />
  );
}
